import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { addProduct } from "../../features/freezerSlice";
import { validateProduct, CATEGORIES } from "../../features/validationUtils";
import styles from "./addProductModal.module.scss";

const AddProductModal = ({ containerId, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    label: "",
    units: 1,
    weight: 0,
    portions: 1,
    freeze_date: new Date().toISOString().split("T")[0],
    dlc: "",
    details: "",
    category: "viande",
    container_id: containerId,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { id: "viande", label: "Viande" },
    { id: "poisson", label: "Poisson" },
    { id: "legumes", label: "Légumes" },
    { id: "plats", label: "Plats Préparés" },
    { id: "desserts", label: "Desserts" },
    { id: "boulangerie", label: "Boulangerie" },
    { id: "divers", label: "Divers" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validation
    const { isValid, errors: validationErrors } = validateProduct(formData);

    if (!isValid) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      await dispatch(addProduct(formData)).unwrap();
      onClose();
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit:
          error.message || "Une erreur est survenue lors de l'ajout du produit",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>Add Product</h2>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="label">Libellé *</label>
            <input
              id="label"
              type="text"
              name="label"
              value={formData.label}
              onChange={handleChange}
              className={errors.label ? styles.errorInput : ""}
            />
            {errors.label && (
              <span className={styles.errorText}>{errors.label}</span>
            )}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="units">Unités *</label>
              <input
                id="units"
                type="number"
                name="units"
                value={formData.units}
                onChange={handleChange}
                min="1"
                className={errors.units ? styles.errorInput : ""}
              />
              {errors.units && (
                <span className={styles.errorText}>{errors.units}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="weight">Poids (g)</label>
              <input
                id="weight"
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                min="0"
                className={errors.weight ? styles.errorInput : ""}
              />
              {errors.weight && (
                <span className={styles.errorText}>{errors.weight}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="portions">Portions *</label>
              <input
                id="portions"
                type="number"
                name="portions"
                value={formData.portions}
                onChange={handleChange}
                min="1"
                required
                className={errors.portions ? styles.errorInput : ""}
              />
              {errors.portions && (
                <span className={styles.errorText}>{errors.portions}</span>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="freeze_date">Date de congélation *</label>
              <input
                id="freeze_date"
                type="date"
                name="freeze_date"
                value={formData.freeze_date}
                onChange={handleChange}
                required
                className={errors.freeze_date ? styles.errorInput : ""}
              />
              {errors.freeze_date && (
                <span className={styles.errorText}>{errors.freeze_date}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="dlc">Date limite de consommation *</label>
              <input
                id="dlc"
                type="date"
                name="dlc"
                value={formData.dlc}
                onChange={handleChange}
                required
                className={errors.dlc ? styles.errorInput : ""}
              />
              {errors.dlc && (
                <span className={styles.errorText}>{errors.dlc}</span>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="details">Détails</label>
            <textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleChange}
              rows="3"
              className={errors.details ? styles.errorInput : ""}
            />
            {errors.details && (
              <span className={styles.errorText}>{errors.details}</span>
            )}
          </div>

          <div className={styles.categoryGroup}>
            <fieldset>
              <legend>Catégorie *</legend>
              <div className={styles.categoryButtons}>
                {categories.map((cat) => (
                  <div key={cat.id} className={styles.categoryOption}>
                    <input
                      type="radio"
                      id={`category-${cat.id}`}
                      name="category"
                      value={cat.id}
                      checked={formData.category === cat.id}
                      onChange={handleChange}
                    />
                    <label
                      htmlFor={`category-${cat.id}`}
                      className={`${styles.categoryLabel} ${
                        formData.category === cat.id ? styles.selected : ""
                      }`}
                    >
                      {cat.label}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          </div>

          {errors.submit && (
            <div className={styles.submitError}>{errors.submit}</div>
          )}

          <div className={styles.modalButtons}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className={styles.cancelButton}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;
